import React, { useEffect, useState } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import Sidebar from "@shared/components/Sidebar/Sidebar";
import { useSidebar } from "@services/SidebarContext";
import DocxEditor from "@pages/Project/components/DocxEditor";
import { HotTable } from "@handsontable/react";
import useNotifications from "@utils/useNotifications";
import JoinProjectModal from "@pages/Project/components/JoinProjectModel";
import { useUser } from "@services/UserContext";
import TitleHeader from "./TitleHeader";
import TermsPrivacyBlockingModal from "@pages/Project/components/TermsPrivacyBlockingModal";

const LayerComponent: React.FC = () => {
  const [chatLog, setChatLog] = useState<
    { message: string; sender: "user" | "bot" }[]
  >([]);
  const [showDocxModal, setShowDocxModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [docxContent, setDocxContent] = useState("");
  const [excelData, setExcelData] = useState<any[][]>([]);
  const { isSidebarDropdownOpened } = useSidebar();
  const notificationContext = useNotifications();

  useEffect(() => {
    const savedChatLog = localStorage.getItem("chatLog");
    if (savedChatLog) {
      setChatLog(JSON.parse(savedChatLog));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("chatLog", JSON.stringify(chatLog));
  }, [chatLog]);

  const { user, setRefresh } = useUser();
  const [showJoinProjectModel, setShowJoinProjectModel] = useState(false);

  useEffect(() => {
    if (user) {
      if (!user.first_name && !user.last_name) {
        setShowJoinProjectModel(true);
      }
    } else {
      setShowJoinProjectModel(false);
    }
  }, [user]);

  const location = useLocation();

  const isResultReportPage = matchPath(
    "/report/:reportId/project/:projectId",
    location.pathname
  );

  return (
    <div className="min-h-screen max-h-screen h-screen flex flex-col overflow-hidden">
      <TitleHeader
        title="DYNAMIC_TITLE"
        notificationsContext={notificationContext}
      />
      <div className="relative w-full flex max-h-[calc(100vh-78px)] h-[calc(100vh-78px)]">
        {!isResultReportPage && (
          <div className="top-0">
            <Sidebar isSidebarDropdownOpened />
          </div>
        )}
        <main className="flex-1 flex flex-col pt-0 ">
          <div
            className={`flex-1 ${
              !isResultReportPage && "px-4"
            } flex-col flex max-h-[calc(100vh-78px)] overflow-y-auto`}
          >
            <Outlet context={notificationContext} />
          </div>
        </main>

        <DocxEditor
          isOpen={showDocxModal}
          initialContent={docxContent}
          setEditorContent={() => {
            /* no-op */
          }}
          onClose={() => setShowDocxModal(false)}
        />
        {showExcelModal && (
          <div
            className={`fixed inset-0 flex items-center justify-center bg-black z-50 transition-opacity duration-300 ease-out ${
              showExcelModal
                ? "opacity-100 bg-opacity-50"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <div className="bg-white rounded-lg shadow-lg w-3/4 h-3/4 flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold">File preview</h2>
                <button
                  onClick={() => setShowExcelModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex-grow overflow-y-auto p-4">
                <HotTable
                  data={excelData}
                  colHeaders={true}
                  rowHeaders={true}
                  licenseKey="non-commercial-and-evaluation"
                  width="100%"
                  height="100%"
                  stretchH="all"
                />
              </div>
            </div>
          </div>
        )}

        {showJoinProjectModel && user && (
          <JoinProjectModal
            onClose={() => setShowJoinProjectModel(false)}
            invitedBy={user.invited_by}
          />
        )}

        {user && (
          <TermsPrivacyBlockingModal
            isOpen={!user.accepted_terms}
            onAccepted={() => {
              // triggers user context to refetch user data
              setRefresh((prev) => prev + 1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LayerComponent;
